import {
  type AnchorHTMLAttributes,
  Children,
  createContext,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type NavigationState = unknown;

type LocationState = {
  pathname: string;
  state: NavigationState;
};

type RouterContextValue = LocationState & {
  navigate: (
    to: string,
    options?: { replace?: boolean; state?: NavigationState },
  ) => void;
};

type RouteProps = {
  children?: ReactNode;
  element: ReactNode;
  index?: boolean;
  path?: string;
};

type Match = {
  element: ReactNode;
  params: Record<string, string>;
};

const RouterContext = createContext<RouterContextValue | null>(null);
const OutletContext = createContext<ReactNode>(null);
const ParamsContext = createContext<Record<string, string>>({});

function currentLocation(): LocationState {
  return {
    pathname: window.location.pathname || "/",
    state: window.history.state?.usr ?? null,
  };
}

function useRouterContext() {
  const context = useContext(RouterContext);
  if (!context)
    throw new Error("Router components must be rendered inside BrowserRouter");
  return context;
}

function normalizePath(path: string) {
  if (!path) return "/";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function matchRoute(pattern: string, pathname: string) {
  if (pattern === "*") return {};
  const patternParts = normalizePath(pattern).split("/").filter(Boolean);
  const pathParts = normalizePath(pathname).split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const patternPart = patternParts[index];
    const pathPart = pathParts[index];
    if (!patternPart || !pathPart) return null;
    if (patternPart.startsWith(":")) {
      params[patternPart.slice(1)] = decodeURIComponent(pathPart);
      continue;
    }
    if (patternPart !== pathPart) return null;
  }
  return params;
}

function routeElements(children: ReactNode) {
  return Children.toArray(children).filter(
    isValidElement,
  ) as ReactElement<RouteProps>[];
}

function resolveRoutes(children: ReactNode, pathname: string): Match | null {
  for (const route of routeElements(children)) {
    const { children: nested, element, index, path } = route.props;
    if (index && normalizePath(pathname) === "/")
      return { element, params: {} };
    if (path) {
      const params = matchRoute(path, pathname);
      if (params) return { element, params };
      continue;
    }
    if (nested) {
      const childMatch = resolveRoutes(nested, pathname);
      if (childMatch) {
        return {
          params: childMatch.params,
          element: (
            <OutletContext.Provider value={childMatch.element}>
              {element}
            </OutletContext.Provider>
          ),
        };
      }
    }
  }
  return null;
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<LocationState>(() =>
    currentLocation(),
  );

  useEffect(() => {
    const onPopState = () => setLocation(currentLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({
      ...location,
      navigate(to, options) {
        const next = normalizePath(to);
        const state = options?.state ?? null;
        const historyState = { usr: state };
        if (options?.replace)
          window.history.replaceState(historyState, "", next);
        else window.history.pushState(historyState, "", next);
        setLocation({ pathname: next, state });
      },
    }),
    [location],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

export function Routes({ children }: { children: ReactNode }) {
  const { pathname } = useRouterContext();
  const match = resolveRoutes(children, pathname);
  return (
    <ParamsContext.Provider value={match?.params ?? {}}>
      {match?.element ?? null}
    </ParamsContext.Provider>
  );
}

export function Route(_props: RouteProps) {
  void _props;
  return null;
}

export function Outlet() {
  return <>{useContext(OutletContext)}</>;
}

export function Navigate({
  replace,
  state,
  to,
}: {
  replace?: boolean;
  state?: NavigationState;
  to: string;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace, state });
  }, [navigate, replace, state, to]);
  return null;
}

export function useLocation() {
  const { pathname, state } = useRouterContext();
  return { pathname, state };
}

export function useNavigate() {
  return useRouterContext().navigate;
}

export function useParams() {
  return useContext(ParamsContext);
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  replace?: boolean;
  state?: NavigationState;
  to: string;
};

export function Link({ onClick, replace, state, to, ...props }: LinkProps) {
  const navigate = useNavigate();
  const href = normalizePath(to);
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      props.target
    ) {
      return;
    }
    event.preventDefault();
    navigate(href, { replace, state });
  };

  return <a {...props} href={href} onClick={handleClick} />;
}

type NavLinkProps = Omit<LinkProps, "className"> & {
  className?: string | ((props: { isActive: boolean }) => string);
  end?: boolean;
};

export function NavLink({ className, end, to, ...props }: NavLinkProps) {
  const { pathname } = useLocation();
  const href = normalizePath(to);
  const isActive = end
    ? pathname === href
    : pathname === href || pathname.startsWith(`${href}/`);
  const resolvedClassName =
    typeof className === "function" ? className({ isActive }) : className;

  return <Link {...props} className={resolvedClassName} to={href} />;
}
